import type { ISaveOffer } from '@makemydeal/dr-dash-bff-types';
import { MV_AUTO_SELECT_PROGRAM_SAVED_BAGGAGE_NAME, type StateTree } from '@makemydeal/dr-dash-types';
import { buildOfferSavePayloadObject } from './offerPayloadUtil';

let lastOfferPayloadObject: ISaveOffer | undefined = undefined;

export const stripAutoStampedBaggage = (payload: ISaveOffer | undefined): ISaveOffer | undefined => {
    if (!payload?.baggage) {
        return payload;
    }
    return {
        ...payload,
        baggage: payload.baggage.filter(({ name }) => name !== MV_AUTO_SELECT_PROGRAM_SAVED_BAGGAGE_NAME)
    };
};

type PenciledInputsByOfferType = Record<string, Record<string, unknown> | null | undefined>;

const getPenciledInputs = (scenario: ISaveOffer): PenciledInputsByOfferType | undefined =>
    scenario.offer?.terms?.penciledInputs as PenciledInputsByOfferType | undefined;

const hasPaymentGridData = (offerInputs: Record<string, unknown> | null | undefined): boolean =>
    Boolean(offerInputs) && Object.prototype.hasOwnProperty.call(offerInputs, 'paymentGridData');

const withoutScenarioGridDataHiddenSince = (baseline: ISaveOffer, current: ISaveOffer): ISaveOffer => {
    const terms = baseline.offer?.terms;
    const penciledInputs = terms?.penciledInputs as PenciledInputsByOfferType | undefined;
    if (!penciledInputs) {
        return baseline;
    }

    const currentPenciledInputs = getPenciledInputs(current);
    let removedAny = false;
    const keptPenciledInputs = Object.entries(penciledInputs).reduce<PenciledInputsByOfferType>(
        (inputs, [offerType, offerInputs]) => {
            if (hasPaymentGridData(offerInputs) && !hasPaymentGridData(currentPenciledInputs?.[offerType])) {
                const { paymentGridData: _paymentGridData, ...remaining } = offerInputs as Record<string, unknown>;
                inputs[offerType] = remaining;
                removedAny = true;
            } else {
                inputs[offerType] = offerInputs;
            }
            return inputs;
        },
        {}
    );

    if (!removedAny) {
        return baseline;
    }

    return {
        ...baseline,
        offer: {
            ...baseline.offer,
            terms: {
                ...terms,
                penciledInputs: keptPenciledInputs
            }
        }
    } as ISaveOffer;
};

const findCurrentAlternate = (
    baselineAlternate: ISaveOffer,
    currentAlternates: ISaveOffer[] | undefined
): ISaveOffer | undefined => {
    const scenarioId = baselineAlternate.meta?.scenarioId;
    if (scenarioId === undefined) {
        return undefined;
    }
    return currentAlternates?.find((currentAlternate) => currentAlternate.meta?.scenarioId === scenarioId);
};

export const withoutGridDataHiddenSince = (
    baseline: ISaveOffer | undefined,
    current: ISaveOffer | undefined
): ISaveOffer | undefined => {
    if (!baseline || !current) {
        return baseline;
    }

    const primary = withoutScenarioGridDataHiddenSince(baseline, current);
    const alternateScenarios = baseline.comparisonInfo?.alternateScenarios;
    if (!alternateScenarios?.length) {
        return primary;
    }

    const currentAlternates = current.comparisonInfo?.alternateScenarios;
    const keptAlternates = alternateScenarios.map((baselineAlternate) => {
        const currentAlternate = findCurrentAlternate(baselineAlternate, currentAlternates);
        return currentAlternate ? withoutScenarioGridDataHiddenSince(baselineAlternate, currentAlternate) : baselineAlternate;
    });
    if (primary === baseline && keptAlternates.every((kept, index) => kept === alternateScenarios[index])) {
        return baseline;
    }

    return {
        ...primary,
        comparisonInfo: {
            ...primary.comparisonInfo,
            alternateScenarios: keptAlternates
        }
    } as ISaveOffer;
};

export const buildComparablePayload = (state: StateTree): ISaveOffer | undefined =>
    stripAutoStampedBaggage(buildOfferSavePayloadObject(state));

export const getLastOfferPayloadObject = (): ISaveOffer | undefined => {
    return lastOfferPayloadObject;
};

export const setLastOfferPayloadObject = (state: StateTree): void => {
    lastOfferPayloadObject = buildComparablePayload(state);
};

export const resetLastOfferPayloadObject = (): void => {
    lastOfferPayloadObject = undefined;
};
